import {navigate} from 'gatsby';
import * as React from 'react';
import {useTranslation} from 'react-i18next';
import * as zipCodes from '@src/utils/zipCodes';

import type {
  SalesMarkets,
  SiteSiteMetadataSupportedZipcodes,
} from '@graphql-types';
import * as styles from './salesEvents.module.scss';

const slugify = (v: string) => v.toLowerCase().replace(/[^a-z]+/g, '-');

export type SalesMarketType = Pick<
  SalesMarkets,
  'id' | 'name' | 'state' | 'counties'
>;

type SupportedZipcodes = Pick<
  SiteSiteMetadataSupportedZipcodes,
  'state' | 'counties' | 'zip' | 'county_codes'
>;

interface GeoLocation {
  zip: string;
  state: string;
  countyCode: string;
  countyName: string;
}

interface SalesEventsFormProps {
  salesMarkets: SalesMarketType[];
  supportedZipCodes: SupportedZipcodes[];
}

const SalesEventsForm: React.FC<SalesEventsFormProps> = props => {
  const {t} = useTranslation('SalesEvents');
  const {supportedZipCodes} = props;
  const [zipCode, setZipCode] = React.useState('');
  const [showCountyDropdown, setShowCountyDropdown] = React.useState(false);
  const [selectedCounty, setSelectedCounty] = React.useState('');
  const [showError, setShowError] = React.useState(false);

  const createSlugKey = (zipcode: string, countyCode: string) =>
    zipcode + '_' + countyCode;

  const slugsByZips = React.useMemo(() => {
    const newSlugsByZips: Record<string, string> = {};
    props.salesMarkets.forEach(salesMarket => {
      const slug = slugify(`${salesMarket.name}-${salesMarket.state}`);

      salesMarket.counties.forEach(county => {
        county.county.zip_codes.forEach(it => {
          newSlugsByZips[createSlugKey(it.zip_code, county.county.code)] = slug;
        });
      });
    });

    return newSlugsByZips;
  }, [props.salesMarkets]);

  const createGeoLocations = (newZipCode: string) => {
    const locations: GeoLocation[] = [];
    supportedZipCodes.forEach(entry => {
      if (entry.zip !== newZipCode) {
        return;
      }
      entry.counties.forEach((it, i) => {
        locations.push({
          countyCode: entry.county_codes[i],
          countyName: it,
          state: entry.state,
          zip: entry.zip,
        });
      });
    });
    locations.sort((a, b) => a.countyName.localeCompare(b.countyName));
    return locations;
  };

  const handleSearch = () => {
    const slugKey = createSlugKey(zipCode, selectedCounty);
    const slug = slugsByZips[createSlugKey(zipCode, selectedCounty)];
    if (!slug) {
      setShowError(true);
      return;
    }

    navigate(`${t('slug')}/${slug}/`);
  };

  const handleZipCodeChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    const newZipcode = e.currentTarget.value;
    setZipCode(newZipcode);
    if (zipCodes.isValid(newZipcode)) {
      const geoLocations: GeoLocation[] = createGeoLocations(newZipcode);
      if (geoLocations.length === 0) {
        setShowError(true);
        setShowCountyDropdown(false);
        setSelectedCounty('');
      } else {
        setSelectedCounty(geoLocations[0].countyCode);
        setShowCountyDropdown(geoLocations.length > 1);
      }
    }
  };

  const onChangeCounty: React.ChangeEventHandler<HTMLSelectElement> = e => {
    setSelectedCounty(e.currentTarget.value);
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.form}>
        <div className={styles.row}>
          <label className={styles.orange}>{t('zipCodeLabel')}</label>
          <input
            type="text"
            pattern="[0-9]*"
            title={t('zipCodeLabel')}
            value={zipCode}
            onChange={handleZipCodeChange}
          />
        </div>
        {showCountyDropdown && (
          <div
            className={`${styles.countyDropdown} ${
              showCountyDropdown ? styles.active : null
            }`}>
            <label className={styles.orange}>{t('selectCountyLabel')}</label>
            <div className={styles.select}>
              <select
                defaultValue={selectedCounty ?? ''}
                onChange={onChangeCounty}
                onBlur={onChangeCounty}>
                {createGeoLocations(zipCode).map(location => {
                  return (
                    <option
                      key={location.countyCode}
                      value={location.countyCode}
                      aria-selected={selectedCounty === location.countyCode}>
                      {t('countyName', {name: location.countyName})},{' '}
                      {location.state}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
        )}
        <div className={styles.row}>
          <button className={styles.button} onClick={handleSearch}>
            {t('submitButtonText')}
          </button>
        </div>
      </div>
      {showError && (
        <div className={styles.row}>
          <p>
            <strong>{t('errorMessage')}</strong>
          </p>
        </div>
      )}
    </div>
  );
};

export default SalesEventsForm;
